import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import {
  useAppNavigate,
  usePaneLocation,
  usePaneParams,
} from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import type { GithubPullRequestEntity } from "@cohesive/api-entities";
import {
  type Entity,
  IntegrationIcon,
  Markdown,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
} from "@cohesive/ui-components";
import { formatDistanceToNow } from "date-fns";
import {
  Code2,
  GitBranch,
  GitCommitHorizontal,
  MessageCircle,
  MessageSquare,
  Target,
  X,
  XCircle,
} from "lucide-react";
import { useCallback } from "react";

export function GitHubPullRequestDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  usePaneParams(ROUTES.GITHUB.PR_DETAIL);
  const paneLocation = usePaneLocation("rightPane");
  const entityFromState = paneLocation?.state?.entity as
    | GithubPullRequestEntity
    | undefined;

  const rawData = paneLocation?.state?.rawData as any;

  const pr = entityFromState?.data
    ? {
        ...entityFromState.data,
        author_avatar_url: rawData?.user?.avatar_url,
        assignees: rawData?.assignees || [],
        labels: rawData?.labels || [],
        milestone: rawData?.milestone,
      }
    : undefined;

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  if (!pr) {
    return (
      <div className="flex flex-col h-full bg-background items-center justify-center">
        <XCircle className="h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">Pull request not found</p>
      </div>
    );
  }

  const isMerged = pr.merged;

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />
          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="github"
              name="GitHub"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {pr.title}
            </span>
          </div>
          <TooltipProvider>
            {entityFromState && (
              <EntityPaneHeaderObjectActions
                entity={entityFromState as Entity}
              />
            )}
            <PaneExpandButton />
            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent>
        <div className="space-y-5">
          {/* Author section */}
          <div className="flex items-start gap-3">
            <Avatar className="h-8 w-8">
              {pr.author_avatar_url && (
                <AvatarImage src={pr.author_avatar_url} alt={pr.author} />
              )}
              <AvatarFallback className="text-sm">
                {pr.author?.charAt(0).toUpperCase() || "?"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap text-sm">
                <span className="font-semibold">{pr.author}</span>
                <span className="text-muted-foreground">
                  {isMerged
                    ? "merged this pull request"
                    : "opened this pull request"}
                </span>
                {(isMerged ? pr.merged_at : pr.created_at) && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground">
                          {formatDistanceToNow(
                            new Date(isMerged ? pr.merged_at! : pr.created_at!),
                            {
                              addSuffix: true,
                            },
                          )}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {new Date(
                          isMerged ? pr.merged_at! : pr.created_at!,
                        ).toLocaleString()}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {pr.updated_at &&
                pr.updated_at !== pr.created_at &&
                !isMerged && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Updated{" "}
                    {formatDistanceToNow(new Date(pr.updated_at), {
                      addSuffix: true,
                    })}
                  </div>
                )}
            </div>
          </div>

          {/* Metadata section */}
          <div className="space-y-3 pb-4 border-b">
            {/* Assignees */}
            {pr.assignees && pr.assignees.length > 0 && (
              <div className="flex items-start gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0">
                  Assignees
                </span>
                <div className="flex flex-wrap gap-2">
                  {pr.assignees.map((assignee: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <Avatar className="h-5 w-5">
                        {assignee.avatar_url && (
                          <AvatarImage
                            src={assignee.avatar_url}
                            alt={assignee.login}
                          />
                        )}
                        <AvatarFallback className="text-[10px]">
                          {assignee.login?.charAt(0).toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <span>{assignee.login}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Branches */}
            {pr.head_ref && pr.base_ref && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0 flex items-center gap-1.5">
                  <GitBranch className="h-3.5 w-3.5" />
                  Branches
                </span>
                <span className="text-sm font-mono">
                  {pr.head_ref} → {pr.base_ref}
                </span>
              </div>
            )}

            {/* Code changes - only show if we have actual stats */}
            {((pr.commits_count !== undefined && pr.commits_count > 0) ||
              (pr.changed_files !== undefined && pr.changed_files > 0) ||
              (pr.additions !== undefined && pr.additions > 0) ||
              (pr.deletions !== undefined && pr.deletions > 0)) && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0 flex items-center gap-1.5">
                  <Code2 className="h-3.5 w-3.5" />
                  Changes
                </span>
                <div className="flex items-center gap-3 text-sm">
                  {pr.commits_count !== undefined && pr.commits_count > 0 && (
                    <div className="flex items-center gap-1">
                      <GitCommitHorizontal className="h-3 w-3 text-muted-foreground" />
                      <span>{pr.commits_count}</span>
                    </div>
                  )}
                  {pr.changed_files !== undefined && pr.changed_files > 0 && (
                    <span>
                      {pr.changed_files}{" "}
                      <span className="text-muted-foreground">
                        file{pr.changed_files !== 1 ? "s" : ""}
                      </span>
                    </span>
                  )}
                  {pr.additions !== undefined && pr.additions > 0 && (
                    <span className="text-lc-active">+{pr.additions}</span>
                  )}
                  {pr.deletions !== undefined && pr.deletions > 0 && (
                    <span className="text-lc-blocked">-{pr.deletions}</span>
                  )}
                </div>
              </div>
            )}

            {/* Labels */}
            {pr.labels && pr.labels.length > 0 && (
              <div className="flex items-start gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0">
                  Labels
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {pr.labels.map((label: any, i: number) => (
                    <TooltipProvider key={i}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="outline"
                            className="text-xs"
                            style={{
                              borderColor: label.color
                                ? `#${label.color}`
                                : undefined,
                              backgroundColor: label.color
                                ? `#${label.color}20`
                                : undefined,
                              color: label.color
                                ? `#${label.color}`
                                : undefined,
                            }}
                          >
                            {label.name}
                          </Badge>
                        </TooltipTrigger>
                        {label.description && (
                          <TooltipContent className="max-w-xs">
                            {label.description}
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            )}

            {/* Milestone */}
            {pr.milestone?.title && (
              <div className="flex items-start gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0 flex items-center gap-1.5">
                  <Target className="h-3.5 w-3.5" />
                  Milestone
                </span>
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium">{pr.milestone.title}</p>
                  {pr.milestone?.open_issues !== undefined &&
                    pr.milestone?.closed_issues !== undefined && (
                      <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-lc-active"
                            style={{
                              width: `${
                                (pr.milestone.closed_issues /
                                  (pr.milestone.open_issues +
                                    pr.milestone.closed_issues)) *
                                100
                              }%`,
                            }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {pr.milestone.closed_issues} /{" "}
                          {pr.milestone.open_issues +
                            pr.milestone.closed_issues}
                        </span>
                      </div>
                    )}
                  {pr.milestone?.due_on && (
                    <p className="text-xs text-muted-foreground">
                      Due{" "}
                      {formatDistanceToNow(new Date(pr.milestone.due_on), {
                        addSuffix: true,
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Comments */}
            {(pr.comments_count !== undefined && pr.comments_count > 0) ||
            (pr.review_comments_count !== undefined &&
              pr.review_comments_count > 0) ? (
              <div className="flex items-start gap-3">
                <span className="text-sm text-muted-foreground w-24 shrink-0 flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Comments
                </span>
                <div className="flex flex-col gap-1 text-sm">
                  {pr.comments_count !== undefined && pr.comments_count > 0 && (
                    <div className="flex items-center gap-1.5">
                      <MessageSquare className="h-3 w-3 text-muted-foreground" />
                      <span>{pr.comments_count}</span>
                    </div>
                  )}
                  {pr.review_comments_count !== undefined &&
                    pr.review_comments_count > 0 && (
                      <div className="flex items-center gap-1.5">
                        <MessageCircle className="h-3 w-3 text-muted-foreground" />
                        <span>
                          {pr.review_comments_count}{" "}
                          <span className="text-muted-foreground text-xs">
                            review
                          </span>
                        </span>
                      </div>
                    )}
                </div>
              </div>
            ) : null}
          </div>

          {/* Description */}
          <div className="prose prose-sm max-w-none dark:prose-invert">
            {pr.body ? (
              <Markdown>{pr.body}</Markdown>
            ) : (
              <p className="text-muted-foreground italic">
                No description provided
              </p>
            )}
          </div>
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
